import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImportExportBar from "@/components/ImportExportBar";

export default function MasterCrud({ endpoint, slug, title, subtitle, icon: Icon, fields, importKey }) {
  const empty = Object.fromEntries(fields.map(([k, , t, o]) => [k, t === "reverse-multi" ? [] : t === "select-static" ? (o?.[0] ?? "") : ""]));
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);
  const [opts, setOpts] = useState({});

  const load = () => api.get(endpoint).then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fields.filter(([, , t]) => t === "select" || t === "reverse-multi").forEach(([k, , , ep]) => {
      api.get(ep).then(r => setOpts(o => ({ ...o, [k]: r.data }))).catch(() => {});
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const payload = {};
      fields.forEach(([k, , t]) => {
        if (t === "reverse-multi" || t === "computed") return;
        payload[k] = t === "number" ? Number(f[k] || 0) : (f[k] || null);
      });
      const res = editId
        ? await api.patch(`${endpoint}/${editId}`, payload)
        : await api.post(endpoint, payload);
      const savedName = res.data.name;
      await Promise.all(fields.filter(([, , t]) => t === "reverse-multi").map(async ([k, , , ep, tf]) => {
        const coll = ep.replace(/^\//, "");
        const selected = f[k] || [];
        const changes = (opts[k] || []).filter(o => selected.includes(o.name || o.hsn_code) !== (o[tf] === savedName));
        await Promise.all(changes.map(o =>
          api.patch(`/set-link/${coll}/${o.id}`, { field: tf, value: selected.includes(o.name || o.hsn_code) ? savedName : null })
        ));
      }));
      toast.success(editId ? "Updated" : "Added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = async (row) => {
    const nf = { ...empty };
    fields.forEach(([k, , t]) => { if (t !== "reverse-multi" && t !== "computed") nf[k] = row[k] ?? ""; });
    await Promise.all(fields.filter(([, , t]) => t === "reverse-multi").map(async ([k, , , ep, tf]) => {
      const r = await api.get(ep);
      setOpts(o => ({ ...o, [k]: r.data }));
      nf[k] = r.data.filter(o => o[tf] === row.name).map(o => o.name || o.hsn_code);
    }));
    setF(nf); setEditId(row.id); setOpen(true);
  };

  const del = async (id) => {
    if (!confirm("Delete this record?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const [pk, pkLabel] = fields[0];

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Icon className="w-3 h-3"/> {subtitle}</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{title}</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); } }}>
          <div className="flex items-center gap-2">
            {importKey && <ImportExportBar masterKey={importKey} onImported={load}/>}
            <DialogTrigger asChild>
              <Button data-testid={`add-${slug}-btn`} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New {title}
              </Button>
            </DialogTrigger>
          </div>
          <DialogContent className="rounded-sm bg-card border-border max-w-md">
            <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} {title}</DialogTitle></DialogHeader>
            <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
              {fields.map(([k, label, t, opt]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
                  {t === "select-static" ? (
                    <Select value={f[k] || ""} onValueChange={(v) => setF(x => ({ ...x, [k]: v }))}>
                      <SelectTrigger data-testid={`${slug}-${k}`} className="mt-1 rounded-sm bg-secondary border-border">
                        <SelectValue placeholder={`Select ${label}`} />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {(opt || []).map(v => (
                          <SelectItem key={v} data-testid={`${slug}-${k}-opt-${v}`} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : t === "reverse-multi" ? (
                    <div data-testid={`${slug}-${k}`} className="mt-1 border border-border rounded-sm bg-secondary max-h-36 overflow-y-auto p-2 space-y-1">
                      {(opts[k] || []).length === 0 && <div className="text-xs text-muted-foreground italic p-1">No options available</div>}
                      {(opts[k] || []).map(o => {
                        const val = o.name || o.hsn_code;
                        const checked = (f[k] || []).includes(val);
                        return (
                          <label key={o.id} data-testid={`${slug}-${k}-opt-${val}`} className="flex items-center gap-2 text-xs cursor-pointer text-muted-foreground hover:text-foreground">
                            <input type="checkbox" checked={checked}
                              onChange={() => setF(x => ({ ...x, [k]: checked ? (x[k] || []).filter(v => v !== val) : [...(x[k] || []), val] }))}
                              className="accent-primary" />
                            {val}
                          </label>
                        );
                      })}
                    </div>
                  ) : t === "select" ? (
                    <Select value={f[k] || ""} onValueChange={(v) => setF(x => ({ ...x, [k]: v }))}>
                      <SelectTrigger data-testid={`${slug}-${k}`} className="mt-1 rounded-sm bg-secondary border-border">
                        <SelectValue placeholder={`Select ${label}`} />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {(opts[k] || []).map(o => (
                          <SelectItem key={o.id} value={o.name || o.hsn_code}>{(o.group_code || o.category_code || o.company_code) ? `${o.group_code || o.category_code || o.company_code} · ${o.name || o.hsn_code}` : (o.name || o.hsn_code)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : t === "computed" ? (
                    <Input
                      data-testid={`${slug}-${k}`}
                      value={opt ? opt(f) : ""}
                      readOnly
                      className="mt-1 rounded-sm bg-secondary/50 border-border text-muted-foreground"
                    />
                  ) : (
                    <Input
                      data-testid={`${slug}-${k}`}
                      type={t === "number" ? "number" : "text"}
                      value={f[k] ?? ""}
                      onChange={(e) => setF(x => ({ ...x, [k]: e.target.value }))}
                      className="mt-1 rounded-sm bg-secondary border-border"
                    />
                  )}
                </div>
              ))}
              <Button data-testid={`save-${slug}-btn`} onClick={save} disabled={!f[pk]}
                className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                {editId ? "Update" : "Save"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              {fields.filter(([, , t]) => t !== "reverse-multi").map(([k, label]) => (
                <th key={k} className="text-left p-3">{label}</th>
              ))}
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={fields.length + 1} className="p-6 text-center text-muted-foreground">No {title.toLowerCase()} records yet</td></tr>
            )}
            {list.map(row => (
              <tr key={row.id} data-testid={`${slug}-row-${row.id}`} className="border-t border-border hover:bg-secondary/50">
                {fields.filter(([, , t]) => t !== "reverse-multi").map(([k, , t, opt]) => (
                  <td key={k} className={`p-3 ${k === pk ? "font-mono text-xs text-primary uppercase" : "text-xs"}`}>
                    {t === "computed" ? `${opt ? opt(row) : 0}%` : t === "number" ? `${row[k] ?? 0}%` : (row[k] || "-")}
                  </td>
                ))}
                <td className="p-3 text-right">
                  <Button data-testid={`edit-${slug}-${row.id}`} size="sm" variant="ghost" onClick={() => edit(row)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-${slug}-${row.id}`} size="sm" variant="ghost" onClick={() => del(row.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
